import React from 'react'
import { Calendar, ChevronDown } from 'lucide-react'

export interface YearMultiSelectProps {
  allFinancialYears: number[]
  fyLabelMap: Record<number, string>
  selectedYears: number[]
  onSelectionChange: (years: number[]) => void
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function YearMultiSelect({
  allFinancialYears,
  fyLabelMap,
  selectedYears,
  onSelectionChange,
  open,
  onOpenChange,
}: YearMultiSelectProps) {
  const toggleYear = (year: number) => {
    if (selectedYears.includes(year)) {
      onSelectionChange(selectedYears.filter(yr => yr !== year))
    } else {
      onSelectionChange([...selectedYears, year].sort((a, b) => a - b))
    }
  }

  const selectAll = () => onSelectionChange([...allFinancialYears])
  const yearsDescending = [...allFinancialYears].sort((a, b) => b - a)
  const latestYear = yearsDescending[0]
  const clearAll = () => {
    onSelectionChange(latestYear != null ? [latestYear] : [])
    onOpenChange(false)
  }

  const allSelected =
    allFinancialYears.length > 0 &&
    selectedYears.length === allFinancialYears.length

  const label =
    selectedYears.length === 0
      ? 'Select years…'
      : allSelected
        ? `All years (${allFinancialYears.length})`
        : selectedYears.length === 1
          ? fyLabelMap[selectedYears[0]] ||
            `FY ${selectedYears[0]}-${String((selectedYears[0] + 1) % 100).padStart(2, '0')}`
          : `${selectedYears.length} years`

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => onOpenChange(!open)}
        className="flex items-center gap-2 px-3 py-2 bg-slate-100 border border-slate-200 rounded-lg text-slate-800 text-[12px] font-medium focus:outline-none focus:ring-2 focus:ring-blue-500 min-w-[11rem]"
      >
        <Calendar className="h-4 w-4 text-violet-600 shrink-0" />
        <span className="flex-1 text-left truncate">{label}</span>
        <ChevronDown
          className={`h-4 w-4 shrink-0 text-slate-500 transition-transform ${open ? 'rotate-180' : ''}`}
        />
      </button>
      {open && (
        <>
          <div
            className="fixed inset-0 z-40"
            aria-hidden
            onClick={() => onOpenChange(false)}
          />
          <div className="absolute left-0 top-full mt-1 z-50 min-w-[240px] max-h-64 overflow-y-auto bg-white border border-slate-200 rounded-lg shadow-lg py-2">
            <div className="px-3 py-1.5 flex justify-between gap-2 border-b border-slate-100">
              <button
                type="button"
                onClick={selectAll}
                className="text-[11px] font-medium text-blue-600 hover:text-blue-700"
              >
                Select all
              </button>
              <button
                type="button"
                onClick={clearAll}
                className="text-[11px] font-medium text-slate-500 hover:text-slate-700"
              >
                Reset to latest
              </button>
            </div>
            {yearsDescending.map(year => (
              <label
                key={year}
                className="flex items-center gap-2 px-3 py-2 hover:bg-slate-50 cursor-pointer"
              >
                <input
                  type="checkbox"
                  checked={selectedYears.includes(year)}
                  onChange={() => toggleYear(year)}
                  className="rounded border-slate-300 text-violet-600 focus:ring-violet-500"
                />
                <span className="text-[13px] text-slate-800">
                  {fyLabelMap[year] ||
                    `FY ${year}-${String((year + 1) % 100).padStart(2, '0')}`}
                </span>
              </label>
            ))}
          </div>
        </>
      )}
    </div>
  )
}
